import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Header from '../common/Header';
import Footer from '../common/Footer';
import Sidebar from '../common/Sidebar';
import VerificationModal from '../auth/VerificationModal';
import ConfirmationModal from '../common/ConfirmationModal';
import ChatBot from '../common/ChatBot';
import { User, Settings, LogOut } from 'lucide-react';
import { USER_ROUTES } from '../../constants/routeConstants';

const Layout = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [verificationStep, setVerificationStep] = useState<
    'email' | 'phone' | null
  >(null);
  const [verificationValue, setVerificationValue] = useState('');
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [location]);

  const handleLogoutClick = () => {
    setLogoutError(null);
    setIsLogoutModalOpen(true);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await logout();
      setIsLogoutModalOpen(false);
    } catch (err: any) {
      setLogoutError(err.message || 'Logout failed. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleVerificationSuccess = async (
    nextStep: 'email' | 'phone' | 'dashboard'
  ) => {
    if (nextStep === 'phone') {
      setVerificationStep('phone');
      setVerificationValue(user?.phone || '');
    } else {
      setVerificationStep(null);
    }
  };

  return (
    <div
      className={`h-screen overflow-hidden ${theme.mode === 'dark' ? 'dark' : 'light'}`}
    >
      <Header
        logo="/Forex-crm-logo.png"
        showMobileMenu
        isMobileMenuOpen={isSidebarOpen}
        onMobileMenuToggle={toggleSidebar}
      >
        <Link
          to={USER_ROUTES.USER_PROFILE}
          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <User className="mr-3 h-4 w-4" />
          Profile
        </Link>

        <Link
          to={USER_ROUTES.USER_SETTINGS}
          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Settings className="mr-3 h-4 w-4" />
          Account Settings
        </Link>

        <div className="border-t border-gray-100"></div>

        <button
          onClick={handleLogoutClick}
          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Logout
        </button>
      </Header>

      <div className="flex h-[calc(100vh-48px)]">
        <Sidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          logo="/Forex-crm-logo.png"
        />
        <main className="flex-1 flex flex-col overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="pt-0 px-2 sm:px-2 lg:px-2 flex-1 overflow-y-auto"
          >
            <div className="max-w-8xl mx-auto">
              <Outlet />
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <VerificationModal
        isOpen={verificationStep !== null}
        type={verificationStep || 'email'}
        value={verificationValue}
        onSuccess={handleVerificationSuccess}
        onClose={() => setVerificationStep(null)}
      />

      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        title={logoutError ? 'Logout Failed' : 'Confirm Logout'}
        message={
          logoutError || 'Are you sure you want to logout of your account?'
        }
        onConfirm={handleLogout}
        onCancel={() => setIsLogoutModalOpen(false)}
        confirmText={logoutError ? 'Retry' : 'Logout'}
        cancelText="Cancel"
        type={logoutError ? 'warning' : 'danger'}
        loading={isLoggingOut}
        loadingText="Logging out..."
      />

      <ChatBot />
    </div>
  );
};

export default Layout;
